import React, { forwardRef, ReactNode, Ref } from "react";
import { Link } from "react-router-dom";

import styled from "styles/styled_components";
import BaseButton, {
  IBaseButtonProps,
  ButtonColor,
} from "components/shared/base_button";
import THEME from "styles/theme";

const StyledButton = styled(BaseButton)`
  padding: ${(props) => {
    const { small, medium } = props.theme.spacing;
    return `${small} ${medium}`;
  }};
  & svg {
    vertical-align: text-bottom;
  }
`;

interface IStyledLabel {
  padLeft: boolean;
  padRight: boolean;
}
const StyledLabel = styled("span")<IStyledLabel>`
  ${({ padLeft, theme }) =>
    padLeft ? `padding-left: ${theme.spacing.xsmall};` : ""}
  ${({ padRight, theme }) =>
    padRight ? `padding-right: ${theme.spacing.xsmall};` : ""}
`;

interface IButtonProps extends IBaseButtonProps {
  disabled?: boolean;
  iconLeft?: JSX.Element;
  iconRight?: JSX.Element;
  isLink?: boolean;
  label: ReactNode;
  linkPath?: string;
  title?: string;
  type?: "button" | "reset" | "submit";
  onClick?: () => void;
}

function handleClick(
  disabled: boolean,
  hasLink: boolean,
  event: React.MouseEvent<HTMLElement>,
  onClick?: () => void
) {
  if (!disabled && onClick) {
    if (!hasLink) {
      // We want to block behaviors like page refresh on form submission
      // but allow for navigation if the button is also a link
      event.preventDefault();
    }
    onClick();
  }
}

const Button = forwardRef(
  (
    {
      buttonColor,
      disabled = false,
      iconLeft,
      iconRight,
      isLink = false,
      label,
      linkPath = "",
      title = "",
      type = "button",
      onClick,
    }: IButtonProps,
    ref
  ) => {
    let button = (
      <StyledButton
        buttonColor={buttonColor}
        className="button"
        disabled={disabled}
        ref={ref as Ref<HTMLButtonElement>}
        title={title}
        type={type}
        onClick={(event: React.MouseEvent<HTMLElement>) =>
          handleClick(disabled, isLink, event, onClick)
        }
      >
        {iconLeft}
        <StyledLabel padLeft={!!iconLeft} padRight={!!iconRight}>
          {label}
        </StyledLabel>
        {iconRight}
      </StyledButton>
    );
    if (isLink) {
      button = (
        <Link style={{ color: THEME.colors.uiLink }} to={linkPath}>
          {button}
        </Link>
      );
    }
    return button;
  }
);

export { Button as default, ButtonColor };
